"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState } from "react";
import { FiCalendar, FiEdit2, FiGift, FiTrash2, FiUser } from "react-icons/fi";
import { formatCurrency } from "@/lib/format-data";
import { GiftCardRecipientInput, GiftCardShopCartLine } from "@/types/shop";
import GiftCardConfiguratorDialog from "./configurator-dialog";
import { formatScheduledSendAt } from "./utils";
const unitPriceFor = (line: GiftCardShopCartLine): number => line.kind === "preset" ? line.unitPriceCents : line.amountCents;
const recipientDisplayName = (r: GiftCardRecipientInput): string => {
  const joined = [r.firstName, r.lastName].map(s => s?.trim()).filter(Boolean).join(" ");
  return joined || r.email?.trim() || r.phone?.trim() || "Recipient";
};
interface GiftCardCartRowProps {
  line: GiftCardShopCartLine;
  /**
   * Optional. When supplied, the row renders its own edit affordance and
   * configurator dialog; on confirm, it calls `onReplace` with the updated
   * line. Omit to render the row as read-only.
   */
  onReplace?: (line: GiftCardShopCartLine) => void;
  /**
   * Optional. When supplied, the row renders a remove (trash) button next
   * to the edit affordance — both share the same grouped pill so their
   * alignment, sizing, and hover treatments stay consistent. Omit to hide
   * the affordance (e.g. on a strictly read-only summary).
   */
  onRemove?: () => void;
}

/**
 * Rich visual identity of a gift-card line, plus an optional inline edit
 * affordance. Generic chrome around the row (quantity stepper, remove
 * button, subtotal) is supplied by the caller — the row only owns its own
 * visual identity and its self-contained edit dialog.
 */
const GiftCardCartRow: React.FC<GiftCardCartRowProps> = t0 => {
  const $ = _c(42);
  const {
    line,
    onReplace,
    onRemove
  } = t0;
  const [isEditing, setIsEditing] = useState(false);
  const hasActions = Boolean(onReplace || onRemove);
  const keepCount = line.quantity - line.recipients.length;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="mt-0.5 flex h-10 w-10 flex-none items-center justify-center rounded-full bg-primary/10 text-primary"><FiGift className="h-5 w-5" /></div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== line.quantity) {
    t2 = line.quantity > 1 && <span className="ml-2 text-body font-normal text-foreground-secondary">× {line.quantity}</span>;
    $[1] = line.quantity;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== line.snapshot.name || $[4] !== t2) {
    t3 = <div className="font-medium text-foreground">{line.snapshot.name}{t2}</div>;
    $[3] = line.snapshot.name;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== line) {
    t4 = formatCurrency(unitPriceFor(line));
    $[6] = line;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = line.quantity > 1 ? " each" : "";
  let t6;
  if ($[8] !== t5) {
    t6 = <span>{t5}</span>;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t4 || $[11] !== t6) {
    t7 = <div className="mt-0.5 text-body text-foreground-secondary">{t4}{t6}</div>;
    $[10] = t4;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t3 || $[14] !== t7) {
    t8 = <div className="min-w-0 flex-1">{t3}{t7}</div>;
    $[13] = t3;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== hasActions || $[17] !== line.snapshot.name || $[18] !== onRemove || $[19] !== onReplace) {
    t9 = hasActions && <div className="flex flex-none items-center gap-0.5">{onReplace && <button type="button" onClick={() => setIsEditing(true)} aria-label={`Edit ${line.snapshot.name}`} className="flex h-7 w-7 cursor-pointer items-center justify-center rounded text-foreground-secondary transition-colors hover:bg-hover hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"><FiEdit2 className="h-3.5 w-3.5" /></button>}{onRemove && <button type="button" onClick={onRemove} aria-label={`Remove ${line.snapshot.name} from cart`} className="flex h-7 w-7 cursor-pointer items-center justify-center rounded text-foreground-secondary transition-colors hover:bg-hover hover:text-status-error-text focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"><FiTrash2 className="h-3.5 w-3.5" /></button>}</div>;
    $[16] = hasActions;
    $[17] = line.snapshot.name;
    $[18] = onRemove;
    $[19] = onReplace;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t8 || $[22] !== t9) {
    t10 = <div className="flex items-start gap-2">{t8}{t9}</div>;
    $[21] = t8;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== line.recipients) {
    t11 = line.recipients.map(_temp);
    $[24] = line.recipients;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== keepCount || $[27] !== line.quantity) {
    t12 = keepCount > 0 && <li className="flex items-start gap-1.5"><FiUser className="mt-0.5 h-3 w-3 flex-none" /><span>{keepCount === line.quantity ? "Keeping for yourself" : `Keeping ${keepCount} for yourself`}</span></li>;
    $[26] = keepCount;
    $[27] = line.quantity;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t11 || $[30] !== t12) {
    t13 = <ul className="space-y-0.5 text-caption text-foreground-secondary">{t11}{t12}</ul>;
    $[29] = t11;
    $[30] = t12;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t10 || $[33] !== t13) {
    t14 = <div className="min-w-0 flex-1 space-y-1.5">{t10}{t13}</div>;
    $[32] = t10;
    $[33] = t13;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== isEditing || $[36] !== line || $[37] !== onReplace) {
    t15 = onReplace && <GiftCardConfiguratorDialog selection={isEditing ? {
      kind: "edit",
      line
    } : null} onConfirm={updated => {
      onReplace(updated);
      setIsEditing(false);
    }} onCancel={() => setIsEditing(false)} />;
    $[35] = isEditing;
    $[36] = line;
    $[37] = onReplace;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== t14 || $[40] !== t15) {
    t16 = <div className="flex flex-1 items-start gap-3">{t1}{t14}{t15}</div>;
    $[39] = t14;
    $[40] = t15;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  return t16;
};
export default GiftCardCartRow;
function _temp(r, i) {
  const scheduled = formatScheduledSendAt(r.scheduledSendAt);
  return <li key={i} className="flex items-start gap-1.5 truncate tabular-nums"><FiCalendar className="mt-0.5 h-3 w-3 flex-none" /><span className="truncate"><span className="text-foreground">{recipientDisplayName(r)}</span><span> — </span><span>{scheduled ? `Send on ${scheduled}` : "Send immediately"}</span></span></li>;
}